import React from 'react';
import PageBanner from '../components/PageBanner';
import { Phone, Clock, Navigation, MessageSquareText, Mail } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';


const ServiceCentres = ({ type = 'service' }) => {
    const { language } = useLanguage();
    const t = translations[language].serviceCentres;
    const allCenters = t.centers;

    // Filter based on type prop
    const filteredCenters = allCenters.filter(center => center.type === type);

    const title = type === 'showroom' ? t.showroomsTitle : t.serviceCentersTitle;

    // Helper to render a single center card
    const CenterCard = ({ center }) => (
        <div key={center.id} className="flex flex-col lg:flex-row gap-8 bg-white shadow-[0_0_50px_rgba(0,0,0,0.05)] rounded-3xl overflow-hidden border border-gray-100 p-2 mb-8">
            {/* LEFT PANEL */}
            <div className={`w-full ${center.mapUrl ? 'lg:w-1/3' : 'lg:w-full'} p-6 space-y-6`}>
                <div className="space-y-4">
                    <h3 className="text-2xl font-bold text-gray-900">{center.name}</h3>

                    {/* Contact Details */}
                    <div className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                        <div className="bg-red-600 text-white px-4 py-2 font-bold flex items-center gap-2">
                            <Phone className="w-4 h-4" /> {t.contactDetails}
                        </div>
                        <div className="p-4 text-gray-700 font-medium space-y-1">
                            {center.contacts.map((contact, index) => {
                                const lowerContact = contact.toLowerCase();
                                if (lowerContact.includes('whatsapp')) {
                                    return (
                                        <div key={index} className="flex items-center gap-3">
                                            <FaWhatsapp className="w-5 h-5 text-green-600" />
                                            <span>{contact.replace(/whatsapp/i, '').trim()}</span>
                                        </div>
                                    );
                                } else if (lowerContact.includes('sms')) {
                                    return (
                                        <div key={index} className="flex items-center gap-3">
                                            <MessageSquareText className="w-5 h-5 text-slate-700" />
                                            <span>{contact.replace(/sms/i, '').trim()}</span>
                                        </div>
                                    );
                                } else if (lowerContact.includes('@')) {
                                    return (
                                        <div key={index} className="flex items-center gap-3">
                                            <Mail className="w-5 h-5 text-red-600" />
                                            <a href={`mailto:${contact.trim()}`} className="hover:text-red-600 transition-colors">
                                                {contact.trim()}
                                            </a>
                                        </div>
                                    );
                                }
                                return (
                                    <div key={index} className="flex items-center gap-3">
                                        <Phone className="w-5 h-5 text-red-600" />
                                        <span>{contact}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Working Timing */}
                    <div className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                        <div className="bg-red-600 text-white px-4 py-2 font-bold flex items-center gap-2">
                            <Clock className="w-4 h-4" /> {t.workingTiming}
                        </div>
                        <div className="p-4 text-gray-700 font-medium space-y-1 text-sm">
                            {center.timing.map((time, idx) => (
                                <div key={idx}>{time}</div>
                            ))}
                        </div>
                    </div>

                    {/* Nearest Landmark */}
                    <div className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                        <div className="bg-red-600 text-white px-4 py-2 font-bold flex items-center gap-2">
                            <Navigation className="w-4 h-4" /> {t.nearestLandmark}
                        </div>
                        <div className="p-4 text-gray-700 font-medium">
                            {center.landmark}
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT PANEL - MAP */}
            {center.mapUrl && (
                <div className="flex-1 bg-gray-100 rounded-2xl overflow-hidden m-2 lg:m-6 shadow-inner relative group ">
                    <iframe
                        src={center.mapUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        className="w-full h-full opacity-90 group-hover:opacity-100 transition-opacity duration-300"
                    ></iframe>
                </div>
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-white pt-20 lg:pt-[120px]">
            <PageBanner
                image="/assets/images/service/service-center.jpg"
                imageAlt={title}
                contentClassName="items-start"
            >
                <div className="flex flex-col items-start mt-auto pb-10">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        viewport={{ once: true }}
                        className="text-2xl md:text-7xl font-bold text-white uppercase tracking-tight drop-shadow-2xl"
                    >
                        {title}
                    </motion.h1>
                    <motion.div
                        initial={{ width: 0, opacity: 0 }}
                        whileInView={{ width: "100px", opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                        viewport={{ once: true }}
                        className="h-0.5 md:h-1.5 bg-red-600 mt-3 md:mt-6 rounded-full box-content border-2 border-transparent"
                    />
                </div>
            </PageBanner>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="mb-12 text-gray-600 leading-relaxed text-lg text-justify max-w-4xl">
                    <p>
                        {type === 'showroom' ? t.salesDesc : t.serviceDesc}
                    </p>
                </div>

                {type === 'showroom' ? (
                    <div>
                        {/* 1. Al Qurum */}
                        {filteredCenters.filter(c => c.id === 1).map(center => (
                            <CenterCard key={center.id} center={center} />
                        ))}

                        {/* 2. Suhar */}
                        {filteredCenters.filter(c => c.id === 2).map(center => (
                            <CenterCard key={center.id} center={center} />
                        ))}

                        {/* Sales Centers Header */}
                        <div className="my-10">
                            <h2 className="text-3xl font-bold text-gray-900 border-l-4 border-red-600 pl-4">
                                Sales Centers
                            </h2>
                        </div>

                        {/* Rest of Showrooms */}
                        {filteredCenters.filter(c => c.id !== 1 && c.id !== 2).map(center => (
                            <CenterCard key={center.id} center={center} />
                        ))}
                    </div>
                ) : (
                    <div>
                        {/* All Service Centers */}
                        {filteredCenters.map(center => (
                            <CenterCard key={center.id} center={center} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ServiceCentres;
